import React, { useState, useContext } from "react";
import styled from "styled-components";
import axios from "axios";
import { GlobalState } from "../../GlobalState";
import { FiEdit2, FiMapPin, FiPhone, FiCheck, FiX } from "react-icons/fi";
import UpdatePassword from "./UpdatePassword";

const Container = styled.div`
  width: 100%;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
  padding: 1rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
     background: linear-gradient(90deg, #107066, #0b5a52);
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #107066;
  box-shadow: 0 2px 8px rgba(16, 112, 102, 0.2);
`;

const InfoContent = styled.div`
  flex: 1;
  padding-top: 0.5rem;
`;

const InfoLabel = styled.p`
  font-size: 0.75rem;
  color: #64748b;
  margin: 0 0 0.25rem 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoText = styled.p`
  font-size: 0.85rem;
  color: #1e293b;
  margin: 0;
  font-weight: 500;
  line-height: 1.5;
`;

const EditButton = styled.button`
  position: absolute;
  bottom: 0.2rem;
  right: 1rem;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 112, 102, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 112, 102, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FormContainer = styled.form`
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border: 2px solid #e2e8f0;
`;

const FormTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  color: #1e293b;
  font-weight: 700;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const InputGroup = styled.div`
  margin-bottom: 1.25rem;
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  color: #475569;
  margin-bottom: 0.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #1e293b;
  transition: all 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #107066;
    background: white;
    box-shadow: 0 0 0 3px rgba(16, 112, 102, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border-radius: 8px;
  border-left: 3px solid #ef4444;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: inherit;

  &:active {
    transform: scale(0.98);
  }
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 112, 102, 0.4);

  &:hover {
    box-shadow: 0 6px 20px rgba(16, 112, 102, 0.5);
    transform: translateY(-2px);
  }
`;

const CancelButton = styled(Button)`
  background: #f1f5f9;
  color: #475569;

  &:hover {
    background: #e2e8f0;
  }
`;

export default function UpdateUserData({ contact, address }) {
  const state = useContext(GlobalState);

  const [values, setValues] = useState({ contact, address });
  const [Err, setErr] = useState("");
  const [showForm, setshowForm] = useState(false);

  const sendUpdate = async () => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API}/user/update_data`,
        {
          contact: values.contact,
          address: values.address,
        },
        {
          headers: { Authorization: state.token[0] },
        }
      );

      if (res.status === 200) {
        setshowForm(false);
        setErr("");
      }
    } catch (err) {
      setErr(err.response.data.error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendUpdate();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleCancel = () => {
    setValues({ contact, address });
    setshowForm(false);
    setErr("");
  };

  if (showForm) {
    return (
      <Container>
        <FormContainer onSubmit={handleSubmit}>
          <FormTitle>Update Contact Information</FormTitle>
          
          <InputGroup>
            <InputLabel>
              <FiMapPin size={16} />
              Address
            </InputLabel>
            <StyledInput
              type="text"
              name="address"
              placeholder="Enter your address"
              onChange={handleInputChange}
              value={values.address}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>
              <FiPhone size={16} />
              Phone Number
            </InputLabel>
            <StyledInput
              type="tel"
              name="contact"
              placeholder="Enter your phone number"
              value={values.contact}
              onChange={handleInputChange}
               maxLength={10}
              required
            />
          </InputGroup>

          {Err && <ErrorMessage>{Err}</ErrorMessage>}

          <ButtonGroup>
            <SubmitButton type="submit">
              {/* <FiCheck size={20} /> */}
              Save
            </SubmitButton>
            <CancelButton type="button" onClick={handleCancel}>
              {/* <FiX size={20} /> */}
              Cancel
            </CancelButton>
          </ButtonGroup>
        </FormContainer>
      </Container>
    );
  }

  return (
    <Container>
      <InfoCard>
        <InfoRow>
          <IconBox>
            <FiMapPin size={20} />
          </IconBox>
          <InfoContent>
            <InfoLabel>Address</InfoLabel>
            <InfoText>{values.address}</InfoText>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <IconBox>
            <FiPhone size={20} />
          </IconBox>
          <InfoContent>
            <InfoLabel>Phone</InfoLabel>
            <InfoText>{values.contact}</InfoText>
          </InfoContent>
        </InfoRow>     
      </InfoCard>

        <EditButton onClick={() => setshowForm(true)}>
          <FiEdit2 size={16} />
        </EditButton>
    </Container>
  );
}